import type { Point } from '../matrix';
import { rectFromPoints, type Rect } from '../rect';
import { drawnPieces, type Segment } from './segments';

/**
 * Exact geometric bounds of normalized segments (stroke width excluded). Cubic
 * extrema come from the roots of the derivative, so curves that bulge beyond their
 * endpoints are measured correctly. Moves that draw nothing do not contribute.
 */
export function segmentsBounds(segments: readonly Segment[]): Rect | null {
  const points: Point[] = [];
  for (const { start, segment } of drawnPieces(segments)) {
    points.push(start);
    if (segment.type === 'L') points.push({ x: segment.x, y: segment.y });
    else if (segment.type === 'C') {
      points.push({ x: segment.x, y: segment.y });
      for (const t of cubicExtremaParameters(start, segment)) {
        points.push(cubicPoint(start, segment, t));
      }
    }
  }
  return rectFromPoints(points);
}

type Cubic = Extract<Segment, { type: 'C' }>;

export function cubicPoint(p0: Point, c: Cubic, t: number): Point {
  const mt = 1 - t;
  const a = mt * mt * mt;
  const b = 3 * mt * mt * t;
  const d = 3 * mt * t * t;
  const e = t * t * t;
  return {
    x: a * p0.x + b * c.x1 + d * c.x2 + e * c.x,
    y: a * p0.y + b * c.y1 + d * c.y2 + e * c.y,
  };
}

function cubicExtremaParameters(p0: Point, c: Cubic): number[] {
  const out: number[] = [];
  for (const [v0, v1, v2, v3] of [
    [p0.x, c.x1, c.x2, c.x],
    [p0.y, c.y1, c.y2, c.y],
  ] as const) {
    // B'(t)/3 = A t² + B t + C with d0 = P1−P0, d1 = P2−P1, d2 = P3−P2.
    const d0 = v1 - v0;
    const d1 = v2 - v1;
    const d2 = v3 - v2;
    const a = d0 - 2 * d1 + d2;
    const b = 2 * (d1 - d0);
    const cc = d0;
    for (const t of solveQuadratic(a, b, cc)) {
      if (t > 0 && t < 1) out.push(t);
    }
  }
  return out;
}

function solveQuadratic(a: number, b: number, c: number): number[] {
  const scale = Math.max(Math.abs(a), Math.abs(b), Math.abs(c));
  if (scale === 0) return [];
  if (Math.abs(a) <= 1e-12 * scale) {
    return Math.abs(b) <= 1e-12 * scale ? [] : [-c / b];
  }
  const disc = b * b - 4 * a * c;
  if (disc < 0) return [];
  if (disc === 0) return [-b / (2 * a)];
  const sq = Math.sqrt(disc);
  // Numerically stable form.
  const q = -0.5 * (b + Math.sign(b || 1) * sq);
  const roots = [q / a];
  if (q !== 0) roots.push(c / q);
  return roots;
}
